import type { ReactElement } from "react";
import { useImageMetadata } from "../../hooks/useImageMetadata";
import { useImageValidation } from "../../hooks/useImageValidation";
import { ImageUploadField } from "./ImageUploadField";

interface MediaStepProps {
  submissionId: string | null;
  formData: {
    posterUrl?: string;
    backdropUrl?: string;
    logoUrl?: string;
    horizontalTileUrl?: string;
    verticalTileUrl?: string;
  };
  onChange: (field: string, value: string) => void;
  mode?: "submission" | "title"; // Which entity type we're editing
}

/**
 * Media Step - Poster, Backdrop, and Logo uploads
 */
// eslint-disable-next-line max-lines-per-function
export function MediaStep({
  submissionId,
  formData,
  onChange,
  mode = "submission",
}: Readonly<MediaStepProps>): ReactElement {
  const { imageMetadata, loading, refetch } = useImageMetadata(submissionId, mode);
  const { validationWarnings } = useImageValidation(submissionId, mode);

  const handleToggle = async () => {
    // Refetch metadata after toggle
    const data = await refetch();
    if (data) {
      // Refresh form data
      if (data.images.poster.current) onChange("posterUrl", data.images.poster.current);
      if (data.images.backdrop.current) onChange("backdropUrl", data.images.backdrop.current);
      if (data.images.logo.current) onChange("logoUrl", data.images.logo.current);
    }
  };

  // Wrapper function to satisfy TypeScript - converts async to void
  const handleToggleWrapper = () => {
    void handleToggle();
  };

  return (
    <div className="space-y-6 pb-12">
      <div>
        <h2 className="text-2xl font-bold text-white mb-2">Visual Assets</h2>
        <p className="text-sm text-neutral-400 pb-6">
          Upload or provide URLs for your film's poster, backdrop, and logo.
        </p>
      </div>

      {loading && (
        <div className="bg-neutral-800 border border-neutral-700 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="animate-spin h-4 w-4 border-2 border-neutral-400 border-t-transparent rounded-full" />
            <p className="text-sm text-neutral-400">Loading image metadata...</p>
          </div>
        </div>
      )}

      <div className="space-y-6">
        <div className="grid grid-cols-3 gap-6 pb-6">
          <div className="col-span-1">
            {/* Poster */}
            <ImageUploadField
              submissionId={submissionId}
              label="Poster"
              type="poster"
              value={formData.posterUrl || null}
              onChange={(url) => onChange("posterUrl", url)}
              recommendedSize=""
              aspectRatio="2:3"
              mode={mode}
              imageSource={imageMetadata?.poster.source}
              tmdbUrl={imageMetadata?.poster.tmdbUrl}
              r2Url={imageMetadata?.poster.r2Url}
              onToggle={handleToggleWrapper}
              persistedWarnings={validationWarnings.poster}
            />
          </div>

          <div className="col-span-2">
            {/* Backdrop */}
            <ImageUploadField
              submissionId={submissionId}
              label="Hero"
              type="backdrop"
              value={formData.backdropUrl || null}
              onChange={(url) => onChange("backdropUrl", url)}
              recommendedSize=""
              aspectRatio="16:9"
              mode={mode}
              imageSource={imageMetadata?.backdrop.source}
              tmdbUrl={imageMetadata?.backdrop.tmdbUrl}
              r2Url={imageMetadata?.backdrop.r2Url}
              onToggle={handleToggleWrapper}
              persistedWarnings={validationWarnings.backdrop}
            />
          </div>
        </div>

        <div className="border-t border-neutral-800" />

        {/* Logo */}
        <div className="pb-6">
          <ImageUploadField
            submissionId={submissionId}
            label="Logo"
            type="logo"
            value={formData.logoUrl || null}
            onChange={(url) => onChange("logoUrl", url)}
            recommendedSize="Transparent PNG / SVG"
            mode={mode}
            imageSource={imageMetadata?.logo.source}
            tmdbUrl={imageMetadata?.logo.tmdbUrl}
            r2Url={imageMetadata?.logo.r2Url}
            onToggle={handleToggleWrapper}
            persistedWarnings={validationWarnings.logo}
          />
        </div>

        {/* Browse Tiles Section */}
        <div className="pt-12 border-t border-neutral-800">
          <h3 className="text-lg font-semibold text-white mb-2">Browse Tiles</h3>
          <p className="text-sm text-neutral-400 mb-6">Additional tile images for grid and carousel displays.</p>

          <div className="grid grid-cols-2 gap-6">
            {/* Horizontal Tile */}
            <div>
              <ImageUploadField
                submissionId={submissionId}
                label="Horizontal Tile"
                type="horizontal-tile"
                value={formData.horizontalTileUrl || null}
                onChange={(url) => onChange("horizontalTileUrl", url)}
                recommendedSize=""
                aspectRatio="16:9"
                mode={mode}
                persistedWarnings={validationWarnings["horizontal-tile"]}
              />
              <p className="text-xs text-neutral-500 mt-2">16:9 aspect ratio for landscape carousels</p>
            </div>

            {/* Vertical Tile */}
            <div>
              <ImageUploadField
                submissionId={submissionId}
                label="Vertical Tile"
                type="vertical-tile"
                value={formData.verticalTileUrl || null}
                onChange={(url) => onChange("verticalTileUrl", url)}
                recommendedSize=""
                aspectRatio="2:3"
                mode={mode}
                persistedWarnings={validationWarnings["vertical-tile"]}
              />
              <p className="text-xs text-neutral-500 mt-2">2:3 aspect ratio for portrait carousels</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
